import type {
  ApiResponse,
  SeoMetadata,
  SerpImageSourceResponse,
  SerpLensResponse,
  ShoppingIntent,
  VisionAPIResponse,
} from '@dupe/common/types'
import type { UIMessage } from 'ai'
import { sql } from 'drizzle-orm'
import {
  boolean,
  index,
  integer,
  jsonb,
  numeric,
  pgMaterializedView,
  pgTable,
  pgView,
  primaryKey,
  smallint,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core'

export enum JobStatus {
  Pending = 'Pending',
  InProgress = 'In-progress',
  Done = 'Done',
  Failed = 'Failed',
  Invalid = 'Invalid',
}

export interface OnboardingData {
  shoppingIntent: string | null
  projectDescription: string
  budget: number | null
  style: string | null
  detectedItems: string[]
}

export const shops = pgTable(
  'shops',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    key: text('key').unique().notNull(),
    slug: text('slug').unique(), // generated column (GENERATED ALWAYS AS (public.slugify(display_name)) STORED)
    displayName: text('display_name'),
    description: text('description'),
    favicon: text('favicon'),
    strategies: text('strategies').array(),
    potentialScam: boolean('potential_scam'),
    verified: boolean('verified'),
    secondhand: boolean('secondhand').default(false),
    coverImage: text('cover_image'),
    bannerImage: text('banner_image'),
    categories: text('categories').array(),
    domains: text('domains').array(),
    type: text('type'),
    platform: text('platform'),
    hasDeals: boolean('has_deals'),
    trustMetadata: jsonb('trust_metadata').$type<Record<string, any>>(),
    affiliate: text('affiliate'),
    parsedTakeRate: jsonb('parsed_take_rate').$type<Record<string, any>>(),
    popularityScore: numeric('popularity_score'),
    assessment: jsonb('assessment')
      .$type<{
        quality?: number
        cost?: number
        value?: number
        description?: string
      }>()
      .default({}),
    metadata: jsonb('metadata')
      .$type<{
        upload_to_ik?: boolean
        using_proxy?: boolean
        potential_scam?: boolean
        hide_details?: boolean
        use_race?: boolean
      }>()
      .default({}),
    seo_metadata: jsonb('seo_metadata').$type<Record<string, any>>(),
    ourPicks: jsonb('our_picks')
      .$type<{
        tier?: number
        count?: number
        regex?: string
        position?: number
      }>()
      .default({}),
    namespace: jsonb('namespace').$type<{
      namespace: string
      tier: string
    }>(),
    search_tokens: text('search_tokens').array(),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
    lastInngestedAt: timestamp('last_inngested_at'),
    processed_at: timestamp('processed_at'),
    verifiedShopKeys: text('verified_shop_keys').array(),
  },
  (table) => {
    return {
      keyIdx: uniqueIndex('shops_key_idx').on(table.key),
      slugIdx: uniqueIndex('shops_slug_idx').on(table.slug),
      displayNameIdx: index('shops_display_name_idx').on(table.displayName),
      verifiedIdx: index('shops_verified_idx').on(table.verified),
      shopsPotentialScamIdx: index('shops_potential_scam_idx').on(
        table.potentialScam
      ),
      verifiedShopKeysIdx: index('shops_verified_shop_keys_idx').on(
        table.verifiedShopKeys
      ),
    }
  }
)

export const shopCategory = pgTable('shop_category', {
  value: text('value').primaryKey(),
  label: text('label'),
  is_furniture: boolean('is_furniture'),
})

export const shopCategorization = pgTable('shop_categorization', {
  id: uuid('id').primaryKey().defaultRandom(),
  shopKey: text('shop_key').references(() => shops.key, {
    onDelete: 'cascade',
    onUpdate: 'cascade',
  }),
  category: text('category').references(() => shopCategory.value, {
    onDelete: 'cascade',
    onUpdate: 'cascade',
  }),
  weight: integer('weight'),
  curation_score: numeric('curation_score'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
})

export const topVerifiedShopsView = pgMaterializedView('top_verified_shops', {
  shopKey: text('shop_key'),
  urlCount: integer('url_count'),
}).existing()

export const urls = pgTable(
  'urls',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: text('user_id'), // NEW - will map to users.id so the user can find all their results regardless of tempUserId e.g on different devices
    // userId: text('user_id').references(() => users.id, {
    //   onDelete: 'cascade',
    //   onUpdate: 'cascade',
    // }), // TODO - after ensure we avoid conflicts add references to users.id
    tempUserId: text('temp_user_id'),
    hash: text('hash').notNull().unique(),
    url: text('url').notNull(),
    shopKey: text('shop_key').references(() => shops.key, {
      onDelete: 'cascade',
      onUpdate: 'cascade',
    }),
    createdAt: timestamp('created_at').defaultNow(),
    source: text('source').default('product-url'),
  },
  (table) => {
    return {
      urlUserIdIdx: uniqueIndex('urls_url_user_id_idx').on(
        table.url,
        table.userId
      ),
      urlTempUserIdIdx: uniqueIndex('urls_url_temp_user_id_idx').on(
        table.url,
        table.tempUserId
      ),
      hashIdx: uniqueIndex('urls_hash_idx').on(table.hash),
      urlIdx: index('urls_url_idx').on(table.url),
      shopKeyIdx: index('ursl_shop_key_idx').on(table.shopKey),
      userIdIdx: index('urls_user_id_idx').on(table.userId),
    }
  }
)

export const results = pgTable(
  'results',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: text('user_id').references(() => users.id, {
      onDelete: 'cascade',
      onUpdate: 'cascade',
    }),
    tempUserId: text('temp_user_id'),
    shopKey: text('shop_key').references(() => shops.key, {
      onDelete: 'cascade',
      onUpdate: 'cascade',
    }),
    hash: text('hash')
      .notNull()
      .unique()
      .references(() => urls.hash, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
      }),
    url: text('url').notNull(),
    title: text('title'),
    image: text('image'),
    description: text('description'),
    pricing: jsonb('pricing').$type<{
      price?: number | string
      currency?: string
    }>(),
    dataDetected: jsonb('dataDetected').$type<ApiResponse['dataDetected']>(),
    shoppingIntent: jsonb('shoppingIntent').$type<ShoppingIntent>(),
    seoMetadata: jsonb('seoMetadata').$type<SeoMetadata>(),
    visualMatches:
      jsonb('visualMatches').$type<SerpLensResponse['visual_matches']>(),
    imageSources:
      jsonb('imageSources').$type<SerpImageSourceResponse['image_sources']>(),
    tags: text('tags').array(),
    pageToken: text('pageToken'),
    strategy: text('strategy'),
    featured: boolean('featured'),
    featuredImage: text('featured_image'),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
    userConversationId: text('user_conversation_id'),
    source: text('source').default('product-url'),
    reason: text('reason'),
  },
  (table) => {
    return {
      reasonIdx: index('results_reason_idx')
        .on(table.reason)
        .where(sql`reason IS NOT NULL`),
      shopKeyIdx: index('results_shop_key_idx').on(table.shopKey),
      uniqueHash: uniqueIndex('results_hash_unique_idx').on(table.hash),
      hashIdx: index('results_hash_idx').on(table.hash),
      // TODO (this is very slow on large table, do it later)
      // shopKeyHashIdx: index('results_shop_key_hash_idx').on(
      //   table.shopKey,
      //   table.hash
      // ),
      createdAtIdx: index('idx_results_created_at').on(table.createdAt),
      updatedAtIdx: index('idx_results_updated_at').on(table.updatedAt),
      hashUserIdIdx: uniqueIndex('results_hash_user_id_idx').on(
        table.hash,
        table.userId
      ),
      hashTempUserIdIdx: uniqueIndex('results_hash_temp_user_id_idx').on(
        table.hash,
        table.tempUserId
      ),
      userIdIdx: index('results_user_id_idx').on(table.userId),
      tempUserIdIdx: index('results_temp_user_id_idx').on(table.tempUserId),
      featuredIdx: index('results_featured_idx')
        .on(table.featured)
        .where(sql`featured = true`),
      seoMetadataIdx: index('idx_results_seo_metadata')
        .on(table.seoMetadata)
        .where(sql`"seoMetadata" IS NOT NULL`),
    }
  }
)

// ============================================================================
// product_categories table
// Stores product category detection results in a separate table with proper indexing
// One-to-one relationship with results table via result_hash foreign key
// ============================================================================
export const productCategories = pgTable(
  'product_categories',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    resultHash: text('result_hash')
      .notNull()
      .unique()
      .references(() => results.hash, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
      }),
    detectedCategory: text('detected_category').notNull(),
    confidence: numeric('confidence', { precision: 3, scale: 2 }).notNull(),
    processingTimeMs: integer('processing_time_ms').notNull(),
    isTimeout: boolean('is_timeout').notNull().default(false),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
  },
  (table) => {
    return {
      resultHashIdx: uniqueIndex('product_categories_result_hash_idx').on(
        table.resultHash
      ),
      categoryIdx: index('product_categories_category_idx').on(
        table.detectedCategory
      ),
      createdAtIdx: index('product_categories_created_at_idx').on(
        table.createdAt
      ),
    }
  }
)

// ============================================================================
// results_for_seo table
// Stores references to results that meet specific SEO criteria (e.g., high qualityScore)
// Populated and maintained by triggers defined in packages/data/docs/results_for_seo_triggers.md
// This table allows for efficient querying of high-quality results without indexing the large results table directly.
// ============================================================================
export const results_for_seo = pgTable(
  'results_for_seo',
  {
    // We use hash as the primary link, matching the results table's common reference key
    resultHash: text('result_hash')
      .primaryKey()
      .references(() => results.hash, {
        onDelete: 'cascade', // Ensure rows are deleted if the corresponding result is deleted
        onUpdate: 'cascade',
      }),
    uniqueUrl: text('unique_url').unique(),
    qualityScore: numeric('quality_score'),
    productCategories: text('product_categories').array(),
    productCategorySlugs: text('product_category_slugs').array(),
    rooms: text('rooms').array(),
    updatedAt: timestamp('updated_at').defaultNow(),
    processedAt: timestamp('processed_at'),
  },
  (table) => {
    return {
      uniqueUrlIdx: uniqueIndex('results_for_seo_unique_url_idx').on(
        table.uniqueUrl
      ),
      // Index for efficiently querying by qualityScore
      qualityScoreIdx: index('results_for_seo_quality_score_idx').on(
        table.qualityScore
      ),
      // Use GIN index for efficient searching within the array
      productCategoriesIdx: index('idx_results_for_seo_product_categories')
        .on(table.productCategories)
        .using(sql`gin`),
      processedAtIdx: index('idx_results_for_seo_processed_at').on(
        table.processedAt
      ),
      roomsIdx: index('idx_results_for_seo_rooms').on(table.rooms),
    }
  }
)

// Define the existing materialized view for type-safe querying
// Drizzle Kit will ignore this when generating migrations because of .existing()
// `packages/data/docs/mv_shops_namespace.md`
export const mvShopsNamespaceCategories = pgMaterializedView(
  'mv_shops_namespace_categories',
  {
    shopKey: text('shop_key'),
    namespace: text('namespace'),
    tier: text('tier'),
    categories: text('categories').array(),
  }
).existing()

// Define the existing materialized view for type-safe querying
// Drizzle Kit will ignore this when generating migrations because of .existing()
// `packages/data/docs/mv_unique_product_categories.md`
export const mvUniqueProductCategories = pgMaterializedView(
  'mv_unique_product_categories',
  {
    uniqueCategory: text('unique_category'),
    categoryCount: integer('category_count'),
    categoryPosition: integer('category_position'),
    parentCategory: text('parent_category'),
  }
).existing()

// Define the existing materialized view for type-safe querying
// Drizzle Kit will ignore this when generating migrations because of .existing()
// `packages/data/docs/mv_unique_rooms.md`
export const mvUniqueRooms = pgMaterializedView('mv_unique_rooms', {
  uniqueRoom: text('unique_room'),
  roomCount: integer('room_count'),
}).existing()

export const restyle = pgTable(
  'restyle',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    hash: text('hash').notNull().unique(),
    image: text('image'),
    userId: text('user_id').references(() => users.id, {
      onDelete: 'cascade',
      onUpdate: 'cascade',
    }),
    tempUserId: text('temp_user_id'),
    metadata: jsonb('metadata').$type<Record<string, any>>(),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
  },
  (table) => {
    return {
      hashIdx: uniqueIndex('restyle_hash_idx').on(table.hash),
      hashUserIdIdx: uniqueIndex('restyle_hash_user_id_idx').on(
        table.hash,
        table.userId
      ),
      userIdIdx: index('restyle_user_id_idx').on(table.userId),
      tempUserIdIdx: index('restyle_temp_user_id_idx').on(table.tempUserId),
    }
  }
)

export const restyleJobs = pgTable(
  'restyle_jobs',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    jobId: text('job_id').notNull().unique(),
    hash: text('hash')
      .notNull()
      .references(() => urls.hash, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
      }),
    userId: text('user_id').references(() => users.id, {
      onDelete: 'cascade',
      onUpdate: 'cascade',
    }),
    tempUserId: text('temp_user_id'),
    status: text('status').notNull().default('processing'),
    style: text('style').notNull(),
    customPrompt: text('custom_prompt'),
    preserveWindowsDoors: boolean('preserve_windows_doors').default(true),
    originalImage: text('original_image').notNull(),
    resultImage: text('result_image'),
    resultImageHighRes: text('result_image_high_res'),
    promptId: text('prompt_id'),
    callbackData: jsonb('callback_data').$type<Record<string, any>>(),
    metadata: jsonb('metadata').$type<Record<string, any>>(),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
    featuredAt: timestamp('featured_at'),
    generateDuration: integer('generate_duration'), // generated column (GENERATED ALWAYS AS (EXTRACT(epoch FROM updated_at - created_at)::integer) STORED)
  },
  (table) => {
    return {
      jobIdIdx: uniqueIndex('restyle_jobs_job_id_idx').on(table.jobId),
      hashIdx: index('restyle_jobs_hash_idx').on(table.hash),
      userIdIdx: index('restyle_jobs_user_id_idx').on(table.userId),
      tempUserIdIdx: index('restyle_jobs_temp_user_id_idx').on(
        table.tempUserId
      ),
      statusIdx: index('restyle_jobs_status_idx').on(table.status),
      createdAtIdx: index('restyle_jobs_created_at_idx').on(table.createdAt),
      featuredAtIdx: index('restyle_jobs_featured_at_idx').on(table.featuredAt),
      styleAndFeaturedAtIdx: index('restyle_jobs_style_featured_at_idx')
        .on(table.style, table.featuredAt)
        .where(sql`${table.featuredAt} IS NOT NULL`),
    }
  }
)

export const projects = pgTable(
  'projects',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: text('user_id').references(() => users.id, {
      onDelete: 'cascade',
      onUpdate: 'cascade',
    }),
    tempUserId: text('temp_user_id'),
    name: text('name'),
    description: text('description'),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
  },
  (table) => {
    return {
      userIdIdx: index('projects_user_id_idx').on(table.userId),
      nameTempUserIdIdx: index('projects_name_temp_user_id_idx').on(
        table.name,
        table.tempUserId
      ),
      nameUserIdIdx: index('projects_name_user_id_idx').on(
        table.name,
        table.userId
      ),
      uniqueUserIdName: uniqueIndex('unique_user_id_name').on(
        table.userId,
        table.name
      ),
      nameIdx: index('projects_name_idx').on(table.name),
    }
  }
)

export const result_projects = pgTable(
  'results_projects',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    projectId: uuid('project_id')
      .notNull()
      .references(() => projects.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
      }),
    resultHash: text('result_hash')
      .notNull()
      .references(() => results.hash, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
      }),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
  },
  (table) => {
    return {
      resultHashIdx: index('results_projects_result_hash_idx').on(
        table.resultHash
      ),
      projectIdIdx: index('results_projects_project_id_idx').on(
        table.projectId
      ),
    }
  }
)

export const inspo_cards = pgTable('inspo_cards', {
  id: uuid('id').primaryKey().defaultRandom(),
  image: text('image').notNull(),
  imageSource: text('image_source'),
  description: text('description'),
  likesCount: integer('likes_count').default(0),
  tags: text('tags').array(),
  order: integer('order').default(-1),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
})

export const inspo_card_products = pgTable('inspo_card_products', {
  id: uuid('id').primaryKey().defaultRandom(),
  url: text('url').notNull().unique(),
  image: text('image').notNull(),
  source: text('source'),
  title: text('title'),
  price: numeric('price'),
  compareToPrice: numeric('compare_to_price'),
  currency: text('currency').default('USD'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
})

export const inspo_card_to_products = pgTable(
  'inspo_card_to_products',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    inspoCardId: uuid('inspo_card_id')
      .notNull()
      .references(() => inspo_cards.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
      }),
    inspoCardProductId: uuid('inspo_card_product_id')
      .notNull()
      .references(() => inspo_card_products.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
      }),
    order: integer('order').default(-1),
    createdAt: timestamp('created_at').defaultNow(),
  },
  (table) => {
    return {
      uniqueConstraint: uniqueIndex('inspo_card_to_products_unique_idx').on(
        table.inspoCardId,
        table.inspoCardProductId
      ),
    }
  }
)

export const results_featured_dupe = pgTable(
  'results_featured_dupe',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    resultHash: text('result_hash')
      .notNull()
      .references(() => results.hash, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
      }),
    shopKey: text('shop_key').references(() => shops.key, {
      onDelete: 'cascade',
      onUpdate: 'cascade',
    }),
    url: text('url').notNull(),
    title: text('title'),
    image: text('image'),
    pricing: jsonb('pricing').$type<{
      price?: number | string
      currency?: string
    }>(),
    createdAt: timestamp('created_at').defaultNow(),
  },
  (table) => {
    return {
      resultHashIdx: index('featured_result_hash_idx').on(table.resultHash),
      uniqueConstraint: uniqueIndex('featured_result_hash_url_unique_idx').on(
        table.resultHash,
        table.url
      ),
    }
  }
)

export const result_issues = pgTable(
  'result_issues',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    resultHash: text('result_hash')
      .notNull()
      .references(() => results.hash, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
      }),
    tempUserId: text('temp_user_id').notNull(),
    userContact: text('user_contact'),
    metadata: jsonb('metadata')
      .$type<{
        hash?: string
        url?: string
        image?: string
        user_email?: string
        error?: string
        ua?: string
        [key: string]: any
      }>()
      .default({}),
    createdAt: timestamp('created_at').defaultNow(),
    addressedAt: timestamp('addressed_at'),
  },
  (table) => {
    return {
      resultHashIdx: index('issues_result_hash_temp_user_id_idx').on(
        table.resultHash,
        table.tempUserId
      ),
      uniqueConstraint: uniqueIndex(
        'issues_result_hash_temp_user_id_unique_idx'
      ).on(table.resultHash, table.tempUserId),
    }
  }
)

export const result_feedback = pgTable(
  'result_feedback',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    resultHash: text('result_hash')
      .notNull()
      .references(() => results.hash, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
      }),
    custom: text('custom').notNull().default(''),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
    reaction: boolean('reaction').notNull().default(false),
    tempUserId: text('temp_user_id').notNull().default(''),
  },
  (table) => {
    return {
      resultHashIdx: index('feedback_result_hash_idx').on(
        table.resultHash,
        table.tempUserId
      ),
      uniqueConstraint: uniqueIndex(
        'result_feedback_tempUserId_resultHash_unique'
      ).on(table.tempUserId, table.resultHash),
    }
  }
)

export const user_contacts = pgTable(
  'user_contacts',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tempUserId: text('temp_user_id').notNull(),
    userContact: text('user_contact').notNull(),
    sendBlueContactId: text('sendblue_contact_id'), // TODO: remove
    createdAt: timestamp('created_at').defaultNow(),
  },
  (table) => {
    return {
      uniqueConstraint: uniqueIndex('user_contacts_tempUserId_unique').on(
        table.tempUserId
      ),
      tempUserIdIdx: index('user_contacts_tempUserId_idx').on(table.tempUserId),
      userContactIdx: index('user_contacts_userContact_idx').on(
        table.userContact
      ),
    }
  }
)

export const user_result_notify = pgTable(
  'user_result_notify',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tempUserId: text('temp_user_id').notNull(),
    resultHash: text('result_hash').notNull(),
    createdAt: timestamp('created_at').defaultNow(),
    sendBlueContactId: text('sendblue_contact_id'), // TODO: remove
    reason: text('reason'),
  },
  (table) => {
    return {
      uniqueConstraint: uniqueIndex(
        'user_result_notify_tempUserId_resultHash_unique'
      ).on(table.tempUserId, table.resultHash),
    }
  }
)

export const visual_matches = pgTable(
  'visual_matches',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    resultHash: text('result_hash')
      .notNull()
      .references(() => results.hash, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
      }),
    visualMatches:
      jsonb('visualMatches').$type<SerpLensResponse['visual_matches']>(),
    visualMatchShopKeys: text('visual_match_shop_keys').array(),
    sourceImage: text('source_image'),
    matchImageHash: text('match_hash').notNull(),
    pageToken: text('pageToken'),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
    source: text('source').default('serp-api-lens'),
    country: text('country').default('us'),
  },
  (table) => {
    return {
      resultHashIdx: index('visual_matches_result_hash_idx').on(
        table.resultHash
      ),
      matchImageHashIdx: uniqueIndex('visual_matches_match_hash_idx').on(
        table.matchImageHash,
        table.source,
        table.country
      ),
      visualMatchShopKeysIdx: index(
        'visual_matches_visual_match_shop_keys_idx'
      ).on(table.visualMatchShopKeys),
    }
  }
)

export const vision_api_results = pgTable(
  'vision_api_results',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    resultHash: text('result_hash')
      .notNull()
      .references(() => results.hash, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
      }),
    url: text('url').notNull().unique(),
    data: jsonb('data').$type<VisionAPIResponse[]>(),
    createdAt: timestamp('created_at').defaultNow(),
    pageToken: text('pageToken'),
  },
  (table) => {
    return {
      urlIdx: uniqueIndex('vision_api_results_url_idx').on(table.url),
      resultHashIdx: index('vision_api_results_result_hash_idx').on(
        table.resultHash
      ),
    }
  }
)

export const user_conversations = pgTable(
  'user_conversations',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    messageId: text('message_id').notNull().unique(),
    messageCreatedAt: timestamp('message_created_at').notNull(),
    object: text('object').notNull(),
    from: text('from').notNull(),
    to: text('to').notNull(),
    direction: text('direction').notNull(),
    body: text('body').notNull(),
    media: jsonb('media').$type<any>().default({}),
    status: text('status').notNull(),
    userId: text('user_id'), // This user id is from OpenPhone do not reference to our users table.
    phoneNumberId: text('phone_number_id'),
    conversationId: text('conversation_id').notNull(),
    createdAt: timestamp('created_at').defaultNow(),
  },
  (table) => {
    return {
      messageIdIdx: uniqueIndex('user_conversations_message_id_idx').on(
        table.messageId
      ),
      conversationIdIdx: index('user_conversations_conversation_id_idx').on(
        table.conversationId
      ),
      fromIdx: index('user_conversations_from_idx').on(table.from),
      toIdx: index('user_conversations_to_idx').on(table.to),
    }
  }
)

export const users = pgTable(
  'users',
  {
    id: text('id').primaryKey(),
    tempUserIds: text('temp_user_ids').array(),
    email: text('email'),
    phoneNumber: text('phone_number'),
    username: text('username'),
    firstName: text('first_name'),
    fullName: text('full_name'),
    picture: text('picture'),
    bio: text('bio'),
    metadata: jsonb('metadata').$type<Record<string, any>>().default({}),
    walletData: jsonb('wallet_data').$type<Record<string, any>>(),
    onboarding_data: jsonb('onboarding_data').$type<OnboardingData>(),
    preferences: text('preferences').array().default([]),
    isAnonymous: boolean('is_anonymous').default(false),
    verified: boolean('verified').default(false),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
    deletedAt: timestamp('deleted_at'),
    inviteCode: text('invite_code'),
    invitedById: text('invited_by_id'),
  },
  (table) => {
    return {
      idUniqueIdx: uniqueIndex('users_id_unique_idx').on(table.id),
      tempUserIdsIdx: index('users_temp_user_ids_idx').on(table.tempUserIds),
      usernameIdx: index('users_username_idx').on(table.username),
      emailIdx: index('users_email_idx').on(table.email),
      walletDataNotNullIdx: index('users_wallet_data_not_null_idx')
        .on(table.walletData)
        .where(sql`wallet_data IS NOT NULL`),
      uniqueInviteCodeIdx: uniqueIndex('users_invite_code_unique_idx').on(
        table.inviteCode
      ),
      invitedByIdIdx: index('users_invited_by_id_idx').on(table.invitedById),
    }
  }
)

export const user_deletions = pgTable(
  'user_deletions',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: text('user_id').notNull().unique(),
    reason: text('reason'),
    metadata: jsonb('metadata').$type<Record<string, any>>().default({}),
    createdAt: timestamp('created_at').defaultNow(),
  },
  (table) => {
    return {
      userIdIdx: uniqueIndex('user_deletions_user_id_idx').on(table.userId),
    }
  }
)

export const collections = pgTable(
  'collections',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    name: text('name').notNull(),
    slug: text('slug').notNull(),
    shortId: text('short_id').notNull().unique(),
    userId: text('user_id')
      .notNull()
      .references(() => users.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
      }),
    description: text('description'),
    private: boolean('private').default(true),
    metadata: jsonb('metadata').$type<Record<string, any>>().default({}),
    seoFields: jsonb('seo_fields').$type<Record<string, any>>().default({}),
    order: numeric('order').default('-1'),
    collectionLink: text('collection_link'),
    createdAt: timestamp('created_at').defaultNow(),
  },
  (table) => {
    return {
      userIdIdx: index('collections_user_id_idx').on(table.userId),
      shortIdIdx: uniqueIndex('collections_short_id_unique_idx').on(
        table.shortId
      ),
      userIdShortIdIdx: uniqueIndex('collections_user_id_short_id_idx').on(
        table.userId,
        table.shortId
      ),
    }
  }
)

export const shopItems = pgTable(
  'shop_items',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    itemId: text('item_id').notNull(),
    shopKey: text('shop_key')
      .notNull()
      .references(() => shops.key, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
      }),
    title: text('title'),
    description: text('description'),
    url: text('url').notNull().notNull(),
    image: text('image'),
    imageMetadata: jsonb('image_metadata')
      .$type<Record<string, any>>()
      .default({}),
    pricing: jsonb('pricing').$type<{
      price?: number | string
      currency?: string
    }>(),
    source: text('source'),
    imageHighres: text('image_highres'),
    availability: text('availability'),
    metadata: jsonb('metadata').$type<Record<string, any>>().default({}),
    categories: text('categories').array(),
    keywords: text('keywords').array(),
    hashtags: text('hashtags').array(),
    imagePrimaryR: smallint('image_primary_r'),
    imagePrimaryG: smallint('image_primary_g'),
    imagePrimaryB: smallint('image_primary_b'),
    imageColorPalette: text('image_color_palette').array(),
    onboardingCategories: text('onboarding_categories').array(),
    createdAt: timestamp('created_at').defaultNow(),
  },
  (table) => {
    return {
      shopItemShopKeyItemIdKey: uniqueIndex(
        'shop_item_shop_key_item_id_key'
      ).on(table.shopKey, table.itemId),
      shopItemUrlKey: index('shop_item_url_key').on(table.url),
      shopItemShopKey: index('shop_item_shop_key').on(table.shopKey),
      shopItemSourceIdx: index('shop_item_source_idx').on(table.source),
    }
  }
)

export const carts = pgTable(
  'carts',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    shortId: text('short_id').notNull(),
    userId: text('user_id')
      .notNull()
      .references(() => users.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
      }),
    createdAt: timestamp('created_at').defaultNow(),
    shopKey: text('shop_key')
      .notNull()
      .references(() => shops.key, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
      }),
  },
  (table) => {
    return {
      cartUserIdShopKey: uniqueIndex('cart_user_id_shop_key').on(
        table.userId,
        table.shopKey
      ),
    }
  }
)

export const cartItems = pgTable(
  'cart_items',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    quantity: numeric('quantity').default('1'),
    itemId: uuid('item_id')
      .notNull()
      .references(() => shopItems.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
      }),
    shopKey: text('shop_key')
      .notNull()
      .references(() => shops.key, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
      }),
    cartId: uuid('cart_id')
      .notNull()
      .references(() => carts.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
      }),
    createdAt: timestamp('created_at').defaultNow(),
    deletedAt: timestamp('deleted_at'),
    source: text('source').notNull().default('save_cart'),
    archivedAt: timestamp('archived_at'),
    metadata: jsonb('metadata').$type<Record<string, any>>().default({}),
    itemTitleOverride: text('item_title_override'),
    itemImageOverride: text('item_image_override'),
    imageMetadata: jsonb('image_metadata')
      .$type<Record<string, any>>()
      .default({}),
    hidden: boolean('hidden').notNull().default(false),
    itemPricingOverride: jsonb('item_pricing_override')
      .$type<Record<string, any>>()
      .default({}),
    itemDescriptionOverride: text('item_description_override'),
    imagePrimaryR: smallint('image_primary_r'),
    imagePrimaryG: smallint('image_primary_g'),
    imagePrimaryB: smallint('image_primary_b'),
    imageColorPalette: text('image_color_palette').array(),
    categoriesOverride: text('categories_override').array(),
  },
  (table) => {
    return {
      cartItemCartIdIndex: index('cart_item_cart_id_index').on(table.cartId),
      cartItemItemIdShopKeyCartIdKey: uniqueIndex(
        'cart_item_item_id_shop_key_cart_id_key'
      ).on(table.itemId, table.shopKey, table.cartId),
      cartItemShopKey: index('cart_item_shop_key').on(table.shopKey),
      cartItemItemIdIndex: index('cart_item_item_id_index').on(table.itemId),
      cartItemSourceIndex: index('cart_item_source_index').on(table.source),
    }
  }
)

export const collectionCartItems = pgTable(
  'collection_cart_items',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    cartItemId: uuid('cart_item_id')
      .notNull()
      .references(() => cartItems.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
      }),
    createdAt: timestamp('created_at').defaultNow(),
    collectionId: uuid('collection_id')
      .notNull()
      .references(() => collections.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
      }),
    order: numeric('order').default('-1'),
    metadata: jsonb('metadata').$type<Record<string, any>>().default({}),
  },
  (table) => {
    return {
      collectionIdCartItemIdIdx: index(
        'collection_cart_items_collection_id_cart_item_id_idx'
      ).on(table.collectionId, table.cartItemId),
    }
  }
)

export const professionals = pgTable(
  'professionals',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    externalId: text('external_id').notNull(),
    externalUrl: text('external_url').notNull(),
    website: text('website'),
    phoneNumber: text('phone_number'),
    email: text('email'),
    businessName: text('business_name'),
    image: text('image'),
    logo: text('logo'),
    description: text('description'),
    address: text('address'),
    socialLinks: text('social_links').array(),
    metadata: jsonb('metadata').$type<Record<string, any>>().default({}),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
    source: text('source').notNull(),
  },
  (table) => {
    return {
      houzzIdIdx: uniqueIndex('professionals_external_id_idx').on(
        table.externalId
      ),
    }
  }
)

export const dupePageViewEvents = pgTable(
  'dupe_page_view_events',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    timestamp: timestamp('timestamp').defaultNow(),
    sessionId: text('session_id'),
    action: text('action'),
    version: text('version'),
    payload: jsonb('payload').$type<Record<string, any>>().default({}),
  },
  (table) => {
    return {
      timestampIdx: index('idx_dupe_page_view_events_timestamp').on(
        table.timestamp
      ),
      sessionIdIdx: index('idx_dupe_page_view_events_session_id').on(
        table.sessionId
      ),
      payloadIdx: index('idx_dupe_page_view_events_payload')
        .on(table.payload)
        .using(sql`gin`),
    }
  }
)

export const extensionUninstallReasons = pgTable(
  'extension_uninstall_reasons',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: text('user_id').notNull(),
    reason: text('reason').notNull(),
    extraFeedback: text('extra_feedback'),
    createdAt: timestamp('created_at').defaultNow(),
  },
  (table) => {
    return {
      reasonIdx: index('uninstall_reasons_reason_idx').on(table.reason),
      userIdIdx: index('uninstall_reasons_user_id_idx').on(table.userId),
    }
  }
)

export const weCanTrackTransactions = pgTable(
  'wecantrack_transactions',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    transactionId: text('transaction_id').notNull(),
    networkId: text('network_id'),
    userNetworkAccountId: integer('user_network_account_id'),
    advertiserId: text('advertiser_id'),
    advertiserName: text('advertiser_name'),
    orderRef: text('order_ref'),
    orderDate: timestamp('order_date'),
    clickDate: timestamp('click_date'),
    currency: text('currency'),
    saleAmount: numeric('sale_amount'),
    saleAmountEuro: numeric('sale_amount_euro'),
    commissionAmount: numeric('commission_amount'),
    commissionAmountEuro: numeric('commission_amount_euro'),
    status: text('status'),
    paid: boolean('paid'),
    declineReason: text('decline_reason'),
    country: text('country'),
    payload: jsonb('payload').$type<Record<string, any>>().default({}),
    lastWctUpdate: timestamp('last_wct_update').notNull(),
    subIds: jsonb('sub_ids').$type<Record<string, any>>().default({}),
    modifiedDate: timestamp('modified_date').notNull(),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
  },
  (table) => {
    return {
      idxWecantrackTransactionId: uniqueIndex(
        'idx_wecantrack_transaction_id'
      ).on(table.transactionId),
      idxWecantrackNetworkId: index('idx_wecantrack_network_id').on(
        table.networkId
      ),
      idxWecantrackAdvertiserId: index('idx_wecantrack_advertiser_id').on(
        table.advertiserId
      ),
      idxWecantrackStatus: index('idx_wecantrack_status').on(table.status),
      idxWecantrackOrderDate: index('idx_wecantrack_order_date').on(
        table.orderDate
      ),
      idxWecantrackClickDate: index('idx_wecantrack_click_date').on(
        table.clickDate
      ),
    }
  }
)

export const strackrTransactions = pgTable(
  'strackr_transactions',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    transactionId: text('transaction_id').notNull(),
    sourceId: text('source_id'),
    networkName: text('network_name'),
    advertiserId: text('advertiser_id'),
    advertiserName: text('advertiser_name'),
    orderId: text('order_id'),
    orderDate: timestamp('order_date'),
    clickDate: timestamp('click_date'),
    currency: text('currency'),
    price: numeric('price'),
    revenue: numeric('revenue'),
    status: text('status'),
    paid: boolean('paid'),
    country: text('country'),
    payload: jsonb('payload').$type<Record<string, any>>().default({}),
    programId: text('program_id'),
    soldDate: timestamp('sold_date'),
    deviceName: text('device_name'),
    osId: text('os_id'),
    osName: text('os_name'),
    eventName: text('event_name'),
    reasonName: text('reason_name'),
    customs: text('customs').array(),
    sourceCurrency: text('source_currency'),
    sourcePrice: numeric('source_price'),
    sourceRevenue: numeric('source_revenue'),
    statusUpdated: timestamp('status_updated'),
    paidUpdated: timestamp('paid_updated'),
    baskets: jsonb('baskets'),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
  },
  (table) => {
    return {
      idxStrackrTransactionId: uniqueIndex('idx_strackr_transaction_id').on(
        table.transactionId
      ),
      idxStrackrAdvertiserId: index('idx_strackr_advertiser_id').on(
        table.advertiserId
      ),
      idxStrackrStatus: index('idx_strackr_status').on(table.status),
      idxStrackrOrderDate: index('idx_strackr_order_date').on(table.orderDate),
      idxStrackrClickDate: index('idx_strackr_click_date').on(table.clickDate),
    }
  }
)

export const linkClicks = pgTable(
  'link_clicks',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    shortId: text('short_id')
      .notNull()
      .default(sql`generate_short_id()`),
    tempUserId: text('temp_user_id'),
    userId: text('user_id'),
    url: text('url').notNull(),
    shopKey: text('shop_key').notNull(),
    hash: text('hash'),
    metadata: jsonb('metadata').$type<Record<string, any>>().default({}),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
  },
  (table) => {
    return {
      linkClicksUrlTempUserIdUniq: uniqueIndex(
        'link_clicks_url_temp_user_id_uniq'
      ).on(table.url, table.tempUserId),
    }
  }
)

export const sentPushEvents = pgTable(
  'sent_pdp_push_events',
  {
    viewId: uuid('view_id').notNull(), // Unique identifier for the page view event
    userId: text('user_id').notNull(), // User ID (session or user identifier)
    itemUrl: text('item_url').notNull(), // URL of the item that was viewed
    shopKey: text('shop_key').notNull(), // Key identifying the shop
    sentAt: timestamp('sent_at').defaultNow(), // Timestamp for when the push was sent
  },
  (table) => {
    return {
      // primary key over user_id and view_id
      userViewIdx: uniqueIndex('sent_push_events_user_view_idx').on(
        table.userId,
        table.viewId
      ),
      // Index for speeding up joins and queries on userId and itemUrl
      userItemIdx: index('sent_push_events_user_item_idx').on(
        table.userId,
        table.itemUrl
      ),
    }
  }
)

export const sentSimilarFindsEmails = pgTable(
  'sent_similar_finds_emails',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: text('user_id').notNull(),
    emailId: text('email_id').notNull(),
    sentAt: timestamp('sent_at').defaultNow(),
  },
  (table) => {
    return {
      userSentAtIdx: index('sent_similar_finds_emails_user_sent_at_idx').on(
        table.userId,
        table.sentAt
      ),
      emailIdIdx: uniqueIndex('sent_similar_finds_emails_email_id_idx').on(
        table.emailId
      ),
    }
  }
)

export const emailSendHistory = pgTable(
  'email_send_history',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: text('user_id').notNull(),
    emailId: text('email_id').notNull(),
    emailType: text('email_type').notNull().$type<EmailType>(),
    sentAt: timestamp('sent_at').defaultNow(),
    failed: boolean('failed'), // nullable - null for existing records, true for failures - used to avoid invalid users from being included in each cron the failed row will exclude the user from the next cron (same as we do for successful sends)
  },
  (table) => {
    return {
      userSentAtIdx: index('email_send_history_user_sent_at_idx').on(
        table.userId,
        table.sentAt
      ),
      emailIdIdx: uniqueIndex('email_send_history_email_id_idx').on(
        table.emailId
      ),
      userTypeSentAtIdx: index('email_send_history_user_type_sent_at_idx').on(
        table.userId,
        table.emailType,
        table.sentAt
      ),
    }
  }
)

export const emailResultSets = pgTable(
  'email_result_sets',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    emailId: text('email_id').notNull(),
    resultHash: text('result_hash').notNull(),
    setIndex: integer('set_index').notNull(),
    createdAt: timestamp('created_at').defaultNow(),
  },
  (table) => {
    return {
      emailResultIdx: uniqueIndex('email_result_sets_email_result_idx').on(
        table.emailId,
        table.resultHash
      ),
      resultHashIdx: index('email_result_sets_result_hash_idx').on(
        table.resultHash
      ),
      emailSetIdx: index('email_result_sets_email_set_idx').on(
        table.emailId,
        table.setIndex
      ),
    }
  }
)

export const jobs = pgTable(
  'jobs',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: text('user_id').notNull(),
    bulkId: text('bulk_id').notNull(),
    status: text('status').$type<JobStatus>().default(JobStatus.Pending),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
  },
  (table) => {
    return {
      idxUserId: index('idx_user_id').on(table.userId),
      idxUserIdBulkIk: index('idx_bulk_id_user_id').on(
        table.userId,
        table.bulkId
      ),
    }
  }
)

export const jobItems = pgTable(
  'job_items',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    jobId: uuid('job_id').references(() => jobs.id, {
      onDelete: 'cascade',
    }),
    hash: text('hash').notNull(),
    url: text('url').notNull(),
    status: text('status').$type<JobStatus>().default(JobStatus.Pending),
    inngestId: text('inngest_id'),
    error: text('error'),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
  },
  (table) => {
    return {
      idxHash: index('idx_job_item_hash').on(table.hash),
      idxInngestId: index('idx_inngest_id').on(table.inngestId),
      idxHashJobIdUnique: uniqueIndex('idx_hash_job_id_unique').on(
        table.hash,
        table.jobId
      ),
    }
  }
)

export const grok_vision_to_query = pgTable(
  'grok_vision_to_query',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    imageUrl: text('image_url').notNull(),
    searchQuery: text('search_query'),
    createdAt: timestamp('created_at').defaultNow(),
  },
  (table) => {
    return {
      imageUrlIdx: uniqueIndex('grok_vision_to_query_image_url_idx').on(
        table.imageUrl
      ),
    }
  }
)

// Email type enum to use in code
export enum EmailType {
  SIMILAR_FINDS = 'similar_finds',
  UNCLAIMED_COINS = 'unclaimed_coins',
  // More examples:
  // PRICE_ALERTS = 'price_alerts',
  // WEEKLY_PICKS = 'weekly_picks',
  // MARKETING = 'marketing',
  // PRODUCT_UPDATES = 'product_updates',
}

export const emailPreferences = pgTable(
  'email_preferences',
  {
    userId: text('user_id').notNull(),
    emailType: text('email_type').notNull(),
    disabled: boolean('disabled'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  (table) => {
    return {
      pk: primaryKey({ columns: [table.userId, table.emailType] }),
    }
  }
)

export const typesenseRemovalQueue = pgTable('typesense_removal_queue', {
  id: uuid('id').primaryKey().defaultRandom(),
  itemUuid: text('item_uuid').notNull(),
  collectionName: text('collection_name').notNull(),
  processed: boolean('processed').default(false),
  createdAt: timestamp('created_at').defaultNow(),
})

export const search_queries = pgTable(
  'search_queries',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: text('user_id'),
    tempUserId: text('temp_user_id'),
    query: text('query').notNull(),
    source: text('source').notNull(),
    sourceId: text('source_id'),
    results: jsonb('results').notNull().default({}),
    searchId: text('search_id'), // generated column (GENERATED ALWAYS AS (results->'search_metadata'->>'id') STORED)
    params: jsonb('params').notNull().default({}),
    hash: text('hash')
      .notNull()
      .default(
        sql`generate_hashid_from_string(source || ':' || query || ':' || params::text)`
      ),
    searchCount: integer('search_count').default(1),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
  },
  (table) => {
    return {
      idxSearchQueriesHash: uniqueIndex('idx_search_queries_hash').on(
        table.hash
      ),
      idxSearchQueriesCreatedAt: index('idx_search_queries_created_at').on(
        table.createdAt
      ),
      idxSearchQueriesSourceId: index('idx_search_queries_source_id').on(
        table.sourceId
      ),
      idxSearchQueriesQuery: index('idx_search_queries_query').on(table.query),
      uidx_search_queries_source_query_params: uniqueIndex(
        'uidx_search_queries_source_query_params'
      ).on(table.source, table.query, table.params),
    }
  }
)

export const activities = pgTable('activities', {
  name: text('name').primaryKey(),
  description: text('description'),
  pointsMultiplier: numeric('points_multiplier').default('1.0'),
  maxCompletions: integer('max_completions'), // null means unlimited, 1 means one-time only
  cooldownSeconds: integer('cooldown_seconds'), // cooldown period in seconds, null means no cooldown
  isActive: boolean('is_active').default(true),
  dailyLimit: integer('daily_limit'),
  createdAt: timestamp('created_at').defaultNow(),
})

export const userSessions = pgTable(
  'user_sessions',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: text('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    visitedDate: timestamp('visited_date').defaultNow(),
  },
  (table) => {
    return {
      userIdIdx: index('idx_user_sessions_user_id').on(table.userId),
      userDayIdIdx: uniqueIndex(
        'idx_user_sessions_unique_user_id_visited_date'
      ).on(table.userId, table.visitedDate),
    }
  }
)

export const usersActivities = pgTable(
  'users_activities',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: text('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    activityName: text('activity_name')
      .notNull()
      .references(() => activities.name),
    expirationTimestamp: timestamp('expiration_timestamp'),
    pointsEarned: numeric('points_earned'),
    refTable: text('ref_table'),
    refId: text('ref_id'),
    claimedAt: timestamp('claimed_at'), // when the activity points were claimed/used
    cashedOutAt: timestamp('cashed_out_at'), // when the activity points were cashed out
    metadata: jsonb('metadata').$type<Record<string, any>>().default({}),
    createdAt: timestamp('created_at').defaultNow(),
  },
  (table) => {
    return {
      userIdIdx: index('idx_users_activities_user_id').on(table.userId),
      activityNameIdx: index('idx_users_activities_activity_name').on(
        table.activityName
      ),
      expirationIdx: index('idx_users_activities_expiration').on(
        table.expirationTimestamp
      ),
      claimedAtIdx: index('idx_users_activities_claimed_at').on(
        table.claimedAt
      ),
      userActivityIdx: index('idx_users_activities_user_activity').on(
        table.userId,
        table.activityName
      ),
      refTableRefIdIdx: index('idx_users_activities_ref_table_ref_id').on(
        table.refTable,
        table.refId
      ),
      userActivityRefIdx: uniqueIndex(
        'users_activities_unique_activity_per_ref'
      ).on(table.userId, table.activityName, table.refTable, table.refId),
      createdAtIdx: index('idx_users_activities_created_at').on(
        table.createdAt
      ),
      userIdCreatedAtIdx: index('idx_users_activities_user_id_created_at').on(
        table.userId,
        table.createdAt
      ),
      userIdActivityNameCreatedAtIdx: index(
        'idx_users_activities_user_id_activity_name_created_at'
      ).on(table.userId, table.activityName, table.createdAt),
      userIdCashedOutAtIdx: index(
        'idx_users_activities_user_id_cashed_out_at'
      ).on(table.userId, table.cashedOutAt),
      userIdClaimedAtCashedOutAtIdx: index(
        'idx_users_activities_user_id_claimed_at_cashed_out_at'
      ).on(table.userId, table.claimedAt, table.cashedOutAt),
    }
  }
)

export const deals = pgTable(
  'deals',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    title: text('title').notNull(),
    description: text('description'),
    advertiserId: text('advertiser_id').notNull(),
    advertiserName: text('advertiser_name').notNull(),
    type: text('type').notNull(),
    status: text('status').notNull(),
    startDate: timestamp('start_date').notNull(),
    endDate: timestamp('end_date'),
    discountPercentage: numeric('discount_percentage'),
    discountAmount: numeric('discount_amount'),
    currency: text('currency'),
    country: text('country').notNull().default('us'),
    links: jsonb('links').notNull().default({}),
    externalId: text('external_id').notNull(),
    source: text('source').notNull(),
    network: text('network'),
    dataDetected: jsonb('data_detected').notNull().default({}),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
  },
  (table) => {
    return {
      uidxDealsNetworkExternalId: uniqueIndex(
        'uidx_deals_network_external_id'
      ).on(table.network, table.externalId),
    }
  }
)

export const configs = pgTable(
  'configs',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    configKey: text('config_key').unique().notNull(),
    configValue: jsonb('config_value').$type<unknown>().notNull(),
    userId: uuid('user_id').references(() => users.id),
    revision: integer('revision').default(1).notNull(),
    configSchema: jsonb('config_schema').$type<unknown>(),
    enabled: boolean('enabled').default(true).notNull(),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
  },
  (table) => {
    return {
      configKeyIdx: uniqueIndex('configs_config_key_idx').on(table.configKey),
      updatedAtIdx: index('configs_updated_at_idx').on(table.updatedAt),
      userIdIdx: index('configs_user_id_idx').on(table.userId),
      revisionIdx: index('configs_revision_idx').on(table.revision),
    }
  }
)

/*
// TODO: come back to this - for better billing subscription management compared to current metadata flow
export const billing_subscriptions = pgTable(
  'billing_subscriptions',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: text('user_id')
      .notNull()
      .references(() => users.id),
    stripeCustomerId: text('stripe_customer_id').notNull(),
    status: text('status').notNull().default('inactive'), // 'active' or 'inactive'
    planDuration: text('plan_duration').notNull(), // 'monthly' or 'yearly'
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
  },
  (table) => {
    return {
      userIdIdx: index('billing_subscriptions_user_id_idx').on(table.userId),
      stripeCustomerIdIdx: index('billing_subscriptions_stripe_customer_id_idx').on(table.stripeCustomerId),
    }
  }
)
*/

// AI Chat Tables

export const chats = pgTable(
  'chats',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: text('user_id'),
    tempUserId: text('temp_user_id'),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
    threadId: text('thread_id').unique().notNull(),
    resultHash: text('result_hash'),
    slug: text('slug'),
    summary: text('summary'),
    messages: jsonb('messages').$type<UIMessage[]>(),
    feedback: boolean('feedback'),
    modelId: text('model_id'),
  },
  (table) => {
    return {
      userIdIdx: index('chats_user_id_idx').on(table.userId),
      tempUserIdIdx: index('chats_temp_user_id_idx').on(table.tempUserId),
      resultHashIdx: index('chats_result_hash_idx').on(table.resultHash),
      threadIdIdx: uniqueIndex('chats_thread_id_idx').on(table.threadId),
      createdAtIdx: index('chats_created_at_idx').on(table.createdAt),
      updatedAtIdx: index('chats_updated_at_idx').on(table.updatedAt),
    }
  }
)

export const chatSample = pgView('chats_sample', {
  thread_id: text('thread_id'),
  user_id: text('user_id'),
  temp_user_id: text('temp_user_id'),
  text: text('text'),
  messages_count: integer('messages_count'),
  updated_at: timestamp('updated_at'),
}).as(sql`
  -- Select a random sample of search inputs from the last 24 hours
  SELECT DISTINCT ON (thread_id)
    thread_id,
    user_id,
    temp_user_id,
    part_value->>'text' as text,
    messages_count,
    updated_at
  FROM (
    SELECT 
      thread_id,
      user_id,
      temp_user_id,
      updated_at,
      jsonb_array_length(messages) as messages_count,
      jsonb_array_elements(msg.value->'parts') as part_value,
      msg_pos,
      part_pos
    FROM chats TABLESAMPLE SYSTEM (10),  -- 10% sample of table
      jsonb_array_elements(messages) WITH ORDINALITY as msg(value, msg_pos),
      jsonb_array_elements(msg.value->'parts') WITH ORDINALITY as parts(part_value, part_pos)
    WHERE msg.value->>'role' = 'user'
      AND updated_at >= NOW() - INTERVAL '24 hours'
  ) parts
  WHERE part_value->>'type' = 'text'
  ORDER BY thread_id, msg_pos ASC, part_pos ASC
`)

// AI Search Evaluation Framework Tables
export const chatsEvalRuns = pgTable(
  'chats_eval_runs',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    evaluationType: text('evaluation_type').notNull(),
    status: text('status').notNull().default('pending'),
    startedAt: timestamp('started_at').defaultNow(),
    completedAt: timestamp('completed_at'),
    sampleSize: integer('sample_size'),
    tokenUsage: jsonb('token_usage').$type<{
      input?: number
      inputCached?: number
      reasoning?: number
      output?: number
      total?: number
    }>(),
    createdAt: timestamp('created_at').defaultNow(),
  },
  (table) => {
    return {
      evaluationTypeIdx: index('evaluation_runs_type_idx').on(
        table.evaluationType
      ),
      statusIdx: index('evaluation_runs_status_idx').on(table.status),
      createdAtIdx: index('evaluation_runs_created_at_idx').on(table.createdAt),
    }
  }
)

export const chatsEvalResults = pgTable(
  'chats_eval_results',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    runId: uuid('run_id')
      .references(() => chatsEvalRuns.id, { onDelete: 'cascade' })
      .notNull(),
    chatThreadId: text('chat_thread_id'),
    evaluationType: text('evaluation_type').notNull(),
    score: numeric('score', { precision: 5, scale: 4 }),
    context: text('context'),
    model: text('model'),
    tokenUsage: jsonb('token_usage').$type<{
      inputTokens?: number
      cachedInputTokens?: number
      reasoningTokens?: number
      outputTokens?: number
      totalTokens?: number
    }>(),
    result: jsonb('result').$type<Record<string, any>>().default({}),
    createdAt: timestamp('created_at').defaultNow(),
  },
  (table) => {
    return {
      runIdIdx: index('evaluation_results_run_id_idx').on(table.runId),
      chatThreadIdIdx: index('evaluation_results_chat_thread_id_idx').on(
        table.chatThreadId
      ),
      evaluationTypeIdx: index('evaluation_results_evaluation_type_idx').on(
        table.evaluationType
      ),
      scoreIdx: index('evaluation_results_score_idx').on(table.score),
    }
  }
)

// ============================================================================
// Trade Tables
// ============================================================================
export const trade_leads = pgTable(
  'trade_leads',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    firstName: text('first_name'),
    lastName: text('last_name'),
    email: text('email'), // Made nullable to support phone-only leads
    phoneNumber: text('phone_number'),
    company: text('company'),
    leadCategory: text('lead_category'),
    source: text('source').default('typeform'),
    status: text('status').default('new'),
    metadata: jsonb('metadata').$type<Record<string, any>>().default({}),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
  },
  (table) => {
    return {
      emailUniqueIdx: uniqueIndex('trade_leads_email_unique').on(table.email),
      phoneUniqueIdx: uniqueIndex('trade_leads_phone_unique').on(
        table.phoneNumber
      ),
      statusIdx: index('trade_leads_status_idx').on(table.status),
      sourceIdx: index('trade_leads_source_idx').on(table.source),
      leadCategoryIdx: index('trade_leads_lead_category_idx').on(
        table.leadCategory
      ),
      createdAtIdx: index('trade_leads_created_at_idx').on(table.createdAt),
    }
  }
)

export const trade_projects = pgTable(
  'trade_projects',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    leadId: uuid('lead_id').references(() => trade_leads.id, {
      onDelete: 'set null',
      onUpdate: 'cascade',
    }),
    userId: text('user_id').references(() => users.id, {
      onDelete: 'set null',
      onUpdate: 'cascade',
    }),
    tempUserId: text('temp_user_id'), // For anonymous users before they become leads
    name: text('name'),
    stage: text('stage').notNull().default('calculator'), // 'calculator' | 'quote' | 'order'
    status: text('status').notNull().default('active'), // 'active' | 'archived' | 'completed'
    metadata: jsonb('metadata').$type<Record<string, any>>().default({}),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
  },
  (table) => {
    return {
      leadIdIdx: index('trade_projects_lead_id_idx').on(table.leadId),
      userIdIdx: index('trade_projects_user_id_idx').on(table.userId),
      tempUserIdIdx: index('trade_projects_temp_user_id_idx').on(
        table.tempUserId
      ),
      stageIdx: index('trade_projects_stage_idx').on(table.stage),
      statusIdx: index('trade_projects_status_idx').on(table.status),
      createdAtIdx: index('trade_projects_created_at_idx').on(table.createdAt),
    }
  }
)

export const trade_results = pgTable(
  'trade_results',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    projectId: uuid('project_id')
      .notNull()
      .references(() => trade_projects.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
      }),
    url: text('url').notNull(),
    title: text('title'),
    image: text('image'),
    pricing: jsonb('pricing').$type<{
      retailPrice?: number | string
      offerPrice?: number | string
      currency?: string
    }>(),
    dataDetected: jsonb('data_detected').$type<ApiResponse['dataDetected']>(),
    metadata: jsonb('metadata').$type<Record<string, any>>().default({}),
    position: integer('position').default(0), // Ordering
    status: text('status').notNull().default('active'), // 'active' 'deleted' 'manual'
    deletedAt: timestamp('deleted_at'), // Soft delete timestamp
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
  },
  (table) => {
    return {
      projectIdIdx: index('trade_results_project_id_idx').on(table.projectId),
      projectIdUrlIdx: uniqueIndex('trade_results_project_url_idx').on(
        table.projectId,
        table.url
      ),
      positionIdx: index('trade_results_position_idx').on(table.position),
      statusIdx: index('trade_results_status_idx').on(table.status),
      projectIdStatusIdx: index('trade_results_project_status_idx').on(
        table.projectId,
        table.status
      ),
    }
  }
)
